import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorText } from '../lib/api';
import { useMe } from '../lib/session';
import { fmtTime } from '../lib/format';
import { Button, Card, CardHeader, cx, Empty, ErrorText, Field, Input, PageHeader, Pill, Select, Stat } from '../components/ui';

/** INT-ACC-06: daily FX rates from the provider, manual overrides, and fetch health. */

interface RateView {
  rate: string;
  date: string;
  source: string;
  isOverride: boolean;
  by: string | null;
  createdAt: string;
}
interface FxAdmin {
  source: 'open.er-api' | 'manual';
  provider: { id: string; name: string; url: string; attribution: string; attributionUrl: string };
  maxAgeDays: number;
  lastSuccessAt: string | null;
  currencies: { currency: string; needed: boolean; reporting: boolean; effective: RateView | null; fetched: RateView | null; override: RateView | null; ageDays: number | null; stale: boolean }[];
  jobs: { id: string; status: string; trigger: string | null; attempts: number; maxAttempts: number; lastError: string | null; runAt: string; createdAt: string; updatedAt: string }[];
}

const SOURCE_LABEL: Record<string, string> = { 'open.er-api': 'open.er-api.com', manual: 'manual override', seed: 'seed data', test: 'test data' };
const TRIGGER_LABEL: Record<string, string> = { daily: 'daily', 'daily-retry': 'daily (second look)', manual: 'refresh now' };
const day = (d: string) => new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${d}T00:00:00Z`));
const todayIso = () => new Date().toISOString().slice(0, 10);

export function FxRates() {
  const me = useMe();
  const qc = useQueryClient();
  const tz = me.data?.timezone ?? 'UTC';
  const q = useQuery({
    queryKey: ['fx-admin'],
    queryFn: () => api<FxAdmin>('/fx/admin'),
    refetchInterval: (query) => (query.state.data?.jobs.some((j) => j.status === 'QUEUED' || j.status === 'RUNNING') ? 3000 : 60_000),
  });
  const [form, setForm] = useState({ currency: 'PKR', rateDate: todayIso(), unitsPerUsd: '' });
  const [msg, setMsg] = useState('');
  const refresh = useMutation({
    mutationFn: () => api<{ message: string }>('/fx/refresh', { method: 'POST' }),
    onSuccess: (r) => {
      setMsg(r.message);
      void qc.invalidateQueries({ queryKey: ['fx-admin'] });
    },
  });
  const override = useMutation({
    mutationFn: () => api<{ currency: string; unitsPerUsd: string; rateDate: string }>('/fx/rates', { method: 'PUT', body: form }),
    onSuccess: (r) => {
      setMsg(`Override saved: 1 USD = ${r.unitsPerUsd} ${r.currency} on ${day(r.rateDate)}. It wins over the fetched rate for that date.`);
      setForm({ ...form, unitsPerUsd: '' });
      void qc.invalidateQueries({ queryKey: ['fx-admin'] });
      void qc.invalidateQueries({ queryKey: ['risk'] });
    },
  });
  const d = q.data;
  const lastJob = d?.jobs[0];
  // a retry queued for later (after the provider's next update) is not a fetch in progress; only due or running ones are
  const pending = d?.jobs.some((j) => j.status === 'RUNNING' || (j.status === 'QUEUED' && new Date(j.runAt).getTime() <= Date.now() + 5_000));
  const staleCount = d?.currencies.filter((c) => c.needed && c.stale).length ?? 0;

  return (
    <>
      <PageHeader
        title="FX rates"
        sub="Units of each currency per 1 USD. Risk checks and floor totals convert through USD; a rate older than the maximum age makes risk checks escalate."
        right={
          <Button variant="primary" onClick={() => refresh.mutate()} disabled={refresh.isPending || d?.source === 'manual' || pending}>
            {pending ? 'Fetching…' : 'Refresh now'}
          </Button>
        }
      />
      <ErrorText error={q.error ? errorText(q.error) : refresh.error ? errorText(refresh.error) : null} />
      {msg && (
        <p role="status" className="m-0 text-sm text-up">
          {msg}
        </p>
      )}
      {d && (
        <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat label="Source" value={d.source === 'manual' ? 'Manual only' : d.provider.name} note={<Link to="/admin/settings#setting-FX_RATE_SOURCE">change in the settings console</Link>} />
          <Stat label="Last successful fetch" value={d.lastSuccessAt ? fmtTime(d.lastSuccessAt, tz, true) : 'never'} note={d.source === 'manual' ? 'automatic fetching is off' : 'checked daily by the worker'} />
          <Stat label="Maximum age" value={`${d.maxAgeDays} day${d.maxAgeDays === 1 ? '' : 's'}`} note="FX_RATE_MAX_AGE_DAYS" />
          <Stat label="Out of date" value={staleCount} tone={staleCount ? 'text-down' : 'text-up'} note={staleCount ? 'currencies the floor uses' : 'all rates are fresh'} />
        </section>
      )}
      {lastJob?.status === 'FAILED' || (lastJob?.status === 'QUEUED' && lastJob.lastError) ? (
        <Card tone="down" className="flex flex-col gap-1 p-4">
          <span className="font-semibold text-down">{lastJob.status === 'FAILED' ? 'The last fetch failed' : `The last fetch failed and will be retried at ${fmtTime(lastJob.runAt, tz, true)}`}</span>
          <span className="text-sm text-sub">{lastJob.lastError}</span>
          <span className="text-sm text-sub">The newest stored rates stay in use. Enter an override below if they are out of date.</span>
        </Card>
      ) : null}

      <Card>
        <CardHeader title="Rates in use" right={d && <a href={d.provider.attributionUrl} target="_blank" rel="noreferrer" className="text-xs">{d.provider.attribution}</a>} />
        <div className="overflow-x-auto">
          <table className="w-full min-w-[340px] border-collapse sm:min-w-[760px]">
            <thead>
              <tr className="text-left text-xs text-muted">
                <th className="px-4 py-2.5 font-medium">Currency</th>
                <th className="px-4 py-2.5 text-right font-medium">In use (per 1 USD)</th>
                <th className="px-4 py-2.5 font-medium">Date</th>
                <th className="hidden px-4 py-2.5 font-medium sm:table-cell">Source</th>
                <th className="hidden px-4 py-2.5 text-right font-medium sm:table-cell">Fetched</th>
                <th className="hidden px-4 py-2.5 text-right font-medium sm:table-cell">Override</th>
              </tr>
            </thead>
            <tbody>
              {d?.currencies.map((c) => (
                <tr key={c.currency} className="border-t border-[#1a1f27]">
                  <td className="px-4 py-3">
                    <span className="font-semibold">{c.currency}</span>
                    <span className="ml-2 text-xs text-muted">{[c.reporting && 'reporting', c.needed ? 'used by accounts/symbols' : !c.reporting && 'not used'].filter(Boolean).join(' · ')}</span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[15px] font-semibold">
                    {c.effective?.rate ?? '—'}
                    {c.effective && (
                      <span className={cx('block font-sans text-xs font-normal sm:hidden', c.effective.isOverride ? 'text-warn' : 'text-muted')}>{c.effective.isOverride ? 'override' : SOURCE_LABEL[c.effective.source] ?? c.effective.source}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {c.effective ? day(c.effective.date) : '—'}{' '}
                    {c.stale ? <Pill tone="down">{c.effective ? `${c.ageDays} days old` : 'no rate'}</Pill> : c.ageDays ? <span className="text-xs text-muted">({c.ageDays} d)</span> : null}
                  </td>
                  <td className="hidden px-4 py-3 sm:table-cell">{c.effective ? <Pill tone={c.effective.isOverride ? 'warn' : 'neutral'}>{c.effective.isOverride ? `override${c.effective.by ? ` · ${c.effective.by}` : ''}` : SOURCE_LABEL[c.effective.source] ?? c.effective.source}</Pill> : '—'}</td>
                  <td className="hidden px-4 py-3 text-right font-mono text-sm text-sub sm:table-cell">{c.fetched ? `${c.fetched.rate} · ${day(c.fetched.date)}` : '—'}</td>
                  <td className={cx('hidden px-4 py-3 text-right font-mono text-sm sm:table-cell', c.override ? 'text-warn' : 'text-muted')}>{c.override ? `${c.override.rate} · ${day(c.override.date)}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {q.isLoading && <Empty>Loading…</Empty>}
        </div>
      </Card>

      <div className="grid items-start gap-3.5 lg:grid-cols-2">
        <Card className="flex flex-col gap-3 p-4">
          <h2 className="m-0 text-[15px] font-semibold">Set an override</h2>
          <p className="m-0 text-sm text-sub">An override wins over the fetched rate for its date, even if the daily fetch runs again afterwards. Every override is in the audit log.</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="Currency">
              <Select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                {(d?.currencies ?? []).map((c) => (
                  <option key={c.currency} value={c.currency}>
                    {c.currency}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Date">
              <Input type="date" value={form.rateDate} max={todayIso()} onChange={(e) => setForm({ ...form, rateDate: e.target.value })} />
            </Field>
            <Field label="Units per 1 USD">
              <Input mono inputMode="decimal" value={form.unitsPerUsd} onChange={(e) => setForm({ ...form, unitsPerUsd: e.target.value })} placeholder="281.40" />
            </Field>
          </div>
          <ErrorText error={override.error ? errorText(override.error) : null} />
          <Button variant="primary" className="self-start" onClick={() => override.mutate()} disabled={!form.unitsPerUsd || override.isPending}>
            Save override
          </Button>
        </Card>

        <Card>
          <CardHeader title="Recent fetches" />
          {!d?.jobs.length && <Empty>{d?.source === 'manual' ? 'Fetching is off (manual source).' : 'No fetch has run yet. The worker checks once a day.'}</Empty>}
          <ul className="m-0 list-none p-0">
            {d?.jobs.map((j) => (
              <li key={j.id} className="flex flex-col gap-1 border-t border-[#1a1f27] px-4 py-2.5 first:border-t-0">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm">
                    {fmtTime(j.createdAt, tz, true)} <span className="text-muted">· {TRIGGER_LABEL[j.trigger ?? ''] ?? j.trigger ?? 'fetch'}</span>
                  </span>
                  <Pill tone={j.status === 'DONE' ? 'up' : j.status === 'FAILED' ? 'down' : 'warn'}>
                    {j.status === 'DONE' ? 'stored' : j.status === 'FAILED' ? `failed after ${j.attempts} tries` : j.status === 'RUNNING' ? 'running' : j.attempts ? `retrying (${j.attempts}/${j.maxAttempts})` : 'queued'}
                  </Pill>
                </div>
                {j.lastError && <span className="text-xs text-down">{j.lastError}</span>}
              </li>
            ))}
          </ul>
        </Card>
      </div>
      {d && (
        <p className="m-0 text-xs text-muted">
          Daily rates: <a href={d.provider.attributionUrl} target="_blank" rel="noreferrer">{d.provider.attribution}</a> (free tier, updated once a day).
        </p>
      )}
    </>
  );
}
